import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronRight, Search, ChevronDown, Loader2 } from 'lucide-react';
import { PageWrapper } from '../components/PageWrapper';
import { getEntries, getSettings, formatShortDate, formatDisplayDate, getEnabledPrompts, isAICompanionEnabled } from '@/utils/storage';
import { generateWeeklySummary } from '@/utils/aiCompanion';

// Capitalize first letter
const cap = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1) : '';
const pickTwo = (items) => [...items].sort(() => Math.random() - 0.5).slice(0, 2);

export const History = () => {
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedMonths, setExpandedMonths] = useState({});
  const [weeklySummary, setWeeklySummary] = useState('');
  const [summaryLoading, setSummaryLoading] = useState(false);
  const settings = getSettings();
  const prompts = getEnabledPrompts();
  const userName = settings?.name || 'friend';

  const getFallbackSummary = (recentEntries) => {
    const entryCount = recentEntries.length;
    if (entryCount === 0) return '';

    const last7Days = new Set();
    for (let i = 0; i < 7; i++) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      last7Days.add(d.toISOString().split('T')[0]);
    }

    const loggedInWeek = recentEntries.filter((entry) => last7Days.has(entry.date)).length;
    const goalWins = recentEntries.reduce((count, entry) => {
      const statuses = Object.values(entry.goals || {});
      return count + statuses.filter((s) => s?.completed || s?.easyActivity).length;
    }, 0);

    const reflectionWins = recentEntries.reduce((count, entry) => {
      const promptDefs = entry.promptSnapshots || prompts;
      const answered = promptDefs.some((p) => {
        const value = entry[p.answerKey];
        return value && value !== '–' && value.trim().length > 0;
      });
      return count + (answered ? 1 : 0);
    }, 0);

    const parts = [];
    if (loggedInWeek > 0) {
      parts.push(`Well done ${userName}, you checked in ${loggedInWeek} day${loggedInWeek === 1 ? '' : 's'} this week.`);
    } else {
      parts.push(`Well done ${userName}, you are still showing up for yourself.`);
    }

    const metricOptions = [];
    metricOptions.push(`You checked in ${loggedInWeek} day${loggedInWeek === 1 ? '' : 's'} this week.`);
    metricOptions.push(`You logged ${goalWins} positive goal win${goalWins === 1 ? '' : 's'}.`);
    metricOptions.push(`You completed reflections on ${reflectionWins} day${reflectionWins === 1 ? '' : 's'}.`);

    pickTwo(metricOptions).forEach((line) => parts.push(line));

    parts.push('Keep going, one kind step at a time.');

    return parts.join(' ');
  };

  const loadEntries = () => {
    const allEntries = getEntries();
    const entriesArray = Object.entries(allEntries)
      .map(([date, entry]) => ({ date, ...entry }))
      .sort((a, b) => b.date.localeCompare(a.date));
    setEntries(entriesArray);
  };

  useEffect(() => {
    loadEntries();

    const handleFocus = () => loadEntries();
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        loadEntries();
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let isActive = true;

    const loadWeeklySummary = async () => {
      if (entries.length === 0) {
        if (isActive) {
          setWeeklySummary('');
          setSummaryLoading(false);
        }
        return;
      }

      const recentEntries = entries.slice(0, 7);
      const fallback = getFallbackSummary(recentEntries);
      const aiEnabled = isAICompanionEnabled();

      if (!aiEnabled) {
        if (isActive) {
          setWeeklySummary(fallback);
          setSummaryLoading(false);
        }
        return;
      }

      if (isActive) {
        setSummaryLoading(true);
      }

      try {
        const result = await generateWeeklySummary(recentEntries.map((entry) => ({ date: entry.date, entry })));

        if (!isActive) return;

        if (result.success && result.response) {
          setWeeklySummary(result.response);
        } else {
          setWeeklySummary(fallback);
        }
      } catch {
        if (isActive) {
          setWeeklySummary(fallback);
        }
      } finally {
        if (isActive) {
          setSummaryLoading(false);
        }
      }
    };

    loadWeeklySummary();

    return () => {
      isActive = false;
    };
  }, [entries]); // eslint-disable-line react-hooks/exhaustive-deps

  // Get goals for a specific entry (using its snapshot or falling back to current)
  const getGoalsForEntry = (entry) => {
    return entry.goalSnapshots || (settings?.goals || []).sort((a, b) => a.order - b.order);
  };

  const getGoalDisplay = (entry, goal) => {
    const status = entry.goals?.[goal.id];
    if (status?.completed) return { text: cap(goal.text), done: true };
    if (status?.easyActivity) return { text: cap(goal.easyText || goal.text), done: true };
    return null;
  };

  const getPreviewText = (entry) => {
    const entryPrompts = entry.promptSnapshots || prompts;
    for (const p of entryPrompts) {
      const val = entry[p.answerKey];
      if (val && val !== '–') {
        return val.length > 60 ? val.substring(0, 60) + '...' : val;
      }
    }
    return null;
  };

  const entrySearchText = (entry) => {
    const goalsText = getGoalsForEntry(entry)
      .map((goal) => {
        const status = entry.goals?.[goal.id];
        if (!status?.completed && !status?.easyActivity) return null;
        return `${goal.text || ''} ${goal.easyText || ''}`;
      })
      .filter(Boolean)
      .join(' ');

    const entryPrompts = entry.promptSnapshots || prompts;
    const reflectionText = entryPrompts
      .map((p) => entry[p.answerKey])
      .filter((v) => v && v !== '–')
      .join(' ');

    return [entry.date, goalsText, reflectionText, entry.aiResponse || '']
      .join(' ')
      .toLowerCase();
  };

  const filteredEntries = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return entries;
    return entries.filter((entry) => entrySearchText(entry).includes(q));
  }, [entries, searchQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  const groupedEntries = useMemo(() => {
    const groups = {};

    filteredEntries.forEach((entry) => {
      const dt = new Date(`${entry.date}T00:00:00`);
      if (Number.isNaN(dt.getTime())) {
        if (!groups['Unknown']) groups['Unknown'] = [];
        groups['Unknown'].push(entry);
        return;
      }

      const key = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`;
      if (!groups[key]) groups[key] = [];
      groups[key].push(entry);
    });

    return Object.entries(groups)
      .sort((a, b) => b[0].localeCompare(a[0]))
      .map(([key, list]) => {
        if (key === 'Unknown') {
          return { key, label: 'Unknown Date', entries: list };
        }

        const [year, month] = key.split('-').map(Number);
        const label = new Date(year, month - 1, 1).toLocaleString('en-US', {
          month: 'long',
          year: 'numeric',
        });

        return { key, label, entries: list };
      });
  }, [filteredEntries]);

  useEffect(() => {
    if (groupedEntries.length === 0) return;

    const isSearching = searchQuery.trim().length > 0;
    if (isSearching) {
      const openAllMatching = {};
      groupedEntries.forEach((group) => {
        openAllMatching[group.key] = true;
      });
      setExpandedMonths(openAllMatching);
      return;
    }

    const now = new Date();
    const currentKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    const initial = {};
    groupedEntries.forEach((group, index) => {
      initial[group.key] = group.key === currentKey || (index === 0 && !groupedEntries.some(g => g.key === currentKey));
    });
    setExpandedMonths(initial);
  }, [groupedEntries, searchQuery]);

  const toggleMonth = (key) => {
    setExpandedMonths((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const expandAllMonths = () => {
    const next = {};
    groupedEntries.forEach((group) => {
      next[group.key] = true;
    });
    setExpandedMonths(next);
  };

  const collapseAllMonths = () => {
    const next = {};
    groupedEntries.forEach((group) => {
      next[group.key] = false;
    });
    setExpandedMonths(next);
  };

  return (
    <PageWrapper className="bg-app">
      <div className="max-w-md mx-auto px-6 py-8">
        {entries.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.06 }}
            className="bg-theme-light rounded-2xl p-5 shadow-ambient border border-theme-secondary mb-4"
            data-testid="history-weekly-summary"
          >
            {summaryLoading ? (
              <div className="flex items-center gap-2 text-secondary">
                <Loader2 size={16} className="animate-spin" />
                <span className="font-body text-sm">Creating your summary...</span>
              </div>
            ) : (
              <p className="font-body text-sm text-primary leading-relaxed">
                {weeklySummary}
              </p>
            )}
          </motion.div>
        )}

        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.08 }}
          className="relative mb-6"
        >
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-secondary" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Serch entries"
            className="w-full rounded-2xl border border-theme-secondary bg-theme-light pl-11 pr-4 py-3 font-body text-sm text-primary placeholder:text-secondary/70 shadow-ambient focus:outline-none focus:ring-2 focus:ring-wisteria-200"
            data-testid="history-search"
          />
        </motion.div>

        {entries.length === 0 ? (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
            className="text-center min-h-[50vh] flex flex-col items-center justify-center" data-testid="empty-history"
          >
            <p className="text-secondary font-body text-sm mb-2">No entries yet</p>
            <p className="text-secondary/60 font-body text-sm">Your daily logs will appear here</p>
          </motion.div>
        ) : filteredEntries.length === 0 ? (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
            className="text-center py-12" data-testid="empty-history-search"
          >
            <p className="text-secondary font-body text-sm mb-1">No matches found</p>
            <p className="text-secondary/60 font-body text-sm">Try a different keyword</p>
          </motion.div>
        ) : (
          <div className="space-y-3">
            {groupedEntries.length > 1 && searchQuery.trim().length === 0 && (
              <div className="flex items-center justify-end gap-2 mb-1">
                <button
                  onClick={expandAllMonths}
                  className="text-xs font-body px-3 py-1.5 rounded-full bg-theme-light border border-theme-secondary text-secondary hover:text-primary transition-colors"
                  data-testid="history-expand-all"
                >
                  Expand all
                </button>
                <button
                  onClick={collapseAllMonths}
                  className="text-xs font-body px-3 py-1.5 rounded-full bg-theme-light border border-theme-secondary text-secondary hover:text-primary transition-colors"
                  data-testid="history-collapse-all"
                >
                  Collapse all
                </button>
              </div>
            )}

            {groupedEntries.map((group, groupIndex) => (
              <div key={group.key} className="space-y-3">
                <motion.button
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: groupIndex * 0.04 }}
                  onClick={() => toggleMonth(group.key)}
                  className="w-full flex items-center justify-between px-1 py-1"
                  data-testid={`history-group-toggle-${group.key}`}
                >
                  <h2 className="font-body text-xs uppercase tracking-[0.16em] text-secondary/70">
                    {group.label}
                  </h2>
                  <ChevronDown
                    size={14}
                    className={`text-secondary transition-transform ${expandedMonths[group.key] ? 'rotate-180' : ''}`}
                  />
                </motion.button>

                {expandedMonths[group.key] && group.entries.map((entry, index) => {
                  const entryGoals = getGoalsForEntry(entry);
                  return (
                    <motion.button
                      key={entry.date}
                      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: (groupIndex + index) * 0.04 }}
                      onClick={() => navigate(`/historical-view/${entry.date}`)}
                      className="w-full bg-theme-light rounded-2xl p-5 shadow-ambient border border-theme-secondary text-left transition-all hover:-translate-y-0.5 hover:shadow-interactive"
                      data-testid={`history-entry-${entry.date}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <p className="font-body text-sm text-secondary mb-3">
                            {formatDisplayDate(entry.date)}
                          </p>

                          {/* Goal statuses using entry's own goals */}
                          {entryGoals.length > 0 ? (
                            <div className="flex flex-wrap gap-2">
                              {entryGoals.map(goal => {
                                const display = getGoalDisplay(entry, goal);
                                if (!display) return null;
                                return (
                                  <span
                                    key={goal.id}
                                    className="text-xs font-body px-2 py-1 rounded-full bg-wisteria-100 text-wisteria-700"
                                  >
                                    ✓ {display.text}
                                  </span>
                                );
                              })}
                            </div>
                          ) : (
                            <p className="text-secondary/60 font-body text-xs">No goals logged</p>
                          )}
                        </div>

                        <ChevronRight size={20} strokeWidth={1.5} className="text-wisteria-300 flex-shrink-0 ml-3" />
                      </div>
                    </motion.button>
                  );
                })}
              </div>
            ))}
          </div>
        )}
      </div>
    </PageWrapper>
  );
};

export default History;